import React, { useCallback, useEffect, useState } from 'react'
import { FaBroom as BroomIcon } from 'react-icons/fa'
import {
  useSequencerStore,
  selectCurrentProject,
  isRichStep,
  FX_IDS,
  type FxId,
  type TrackerRow,
  type Project,
  type StepCell,
  type Envelope,
  type RollSubdivisions,
  type StepRoll,
} from '@/stores/sequencerStore'
import { KITS } from '@/hooks/useAudioEngine'
import { CellVisualizer } from '@/components/sequencer/tracker/CellVisualizer'

const ROLL_SUBDIVISIONS: readonly RollSubdivisions[] = [2, 3, 4, 6, 8, 12, 16]

/**
 * Tiny localStorage-backed boolean hook used for per-section
 * collapse state. SSR-safe — ignores the persisted read on the
 * server and falls back to `initial`. Writes are fire-and-forget;
 * if storage is full or blocked (private browsing on Safari) we
 * silently drop the write and let the user re-collapse per-session.
 */
function useStickyBool(
  key: string,
  initial: boolean,
): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    if (typeof window === 'undefined') return initial
    try {
      const raw = window.localStorage.getItem(key)
      if (raw === '1') return true
      if (raw === '0') return false
    } catch {
      /* storage access failed — fall through to initial */
    }
    return initial
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(key, value ? '1' : '0')
    } catch {
      /* quota / blocked storage — drop silently */
    }
  }, [key, value])
  const set = useCallback((next: boolean) => setValue(next), [])
  return [value, set]
}

/**
 * Collapsible section wrapper for the inspector. Every section in
 * the cell editor renders through this so headers / caret / dot /
 * value-chip treatment stays consistent. The `activeDot` prop
 * surfaces "this section has an override" without forcing the
 * user to open it.
 */
const InspectorSection: React.FC<{
  storageKey: string
  label: string
  defaultOpen?: boolean
  activeDot?: boolean
  valueChip?: string
  children: React.ReactNode
}> = ({
  storageKey,
  label,
  defaultOpen = true,
  activeDot = false,
  valueChip,
  children,
}) => {
  const [open, setOpen] = useStickyBool(storageKey, defaultOpen)
  return (
    <div className="border-t border-brd-subtle pt-3 first:border-t-0 first:pt-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary hover:text-content-secondary"
      >
        <span className="flex items-center gap-2">
          <span>{label}</span>
          {activeDot && <span className="text-accent">●</span>}
          {valueChip && (
            <span className="text-content-secondary normal-case tracking-normal">
              {valueChip}
            </span>
          )}
        </span>
        <span aria-hidden>{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="mt-3 flex flex-col gap-3">{children}</div>}
    </div>
  )
}

/** Length presets surfaced alongside the step-length slider. Keyed
 *  by label so the inspector reads naturally; the value is in
 *  16th-note units (the scheduler's native unit). */
const LENGTH_PRESETS: ReadonlyArray<{ label: string; value: number }> = [
  { label: '1', value: 1 },
  { label: '1·⅓ (tri)', value: 2 / 3 },
  { label: '1.5 (dot)', value: 1.5 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
]

/**
 * Side-panel inspector for Tracker mode. Shows and edits the
 * currently-focused cell's per-step data (velocity / pitch / FX
 * sends). On cells with no focus, prompts the user to click one.
 *
 * Per-step FX support is limited to the four parallel wets:
 * delay / reverb / bitcrush / stutter. Filter and tape-stop are
 * serial master-bus effects that can't sensibly send from a
 * single step — they're shown as disabled with a note.
 */
const PER_STEP_FX_SUPPORTED: readonly FxId[] = [
  'delay',
  'reverb',
  'bitcrush',
  'stutter',
]
const FX_PER_STEP_LABEL: Record<FxId, string> = {
  filter: 'Filter',
  delay: 'Delay',
  reverb: 'Reverb',
  bitcrush: 'Bitcrush',
  tapeStop: 'Tape stop',
  stutter: 'Stutter',
}

function rowMeta(
  project: Project,
  row: TrackerRow,
): {
  label: string
  subLabel: string
  cells: StepCell[]
  noteInfo: string
} {
  if (row.kind === 'kit') {
    const kit = KITS[project.kitId]
    const voice = kit.voices[row.padIndex]
    return {
      label: voice?.label ?? `Pad ${row.padIndex + 1}`,
      subLabel: `Pad ${row.padIndex + 1} · kit`,
      cells: project.steps[row.padIndex] ?? [],
      noteInfo: `MIDI note ${voice?.midiNote ?? '—'}`,
    }
  }
  const mp = project.midiPads.find(m => m.id === row.padId)
  return {
    label: mp?.label ?? 'MIDI pad',
    subLabel: 'Custom MIDI pad',
    cells: project.midiPadSteps[row.padId] ?? [],
    noteInfo: mp ? `Note ${mp.note} · ch ${mp.channel}` : '—',
  }
}

export const TrackerInspector: React.FC = () => {
  const focus = useSequencerStore(s => s.trackerFocus)
  const project = useSequencerStore(selectCurrentProject)
  const toggleStep = useSequencerStore(s => s.toggleStep)
  const setStepFx = useSequencerStore(s => s.setStepFx)
  const setStepLength = useSequencerStore(s => s.setStepLength)
  const setStepReverse = useSequencerStore(s => s.setStepReverse)
  const setStepTimeStretch = useSequencerStore(s => s.setStepTimeStretch)
  const setStepEnvelope = useSequencerStore(s => s.setStepEnvelope)
  const setStepRoll = useSequencerStore(s => s.setStepRoll)
  const clearStepExtras = useSequencerStore(s => s.clearStepExtras)

  if (!focus) {
    return (
      <div className="rounded-xl bg-surface-sunken border border-brd-subtle p-4 text-xs text-content-tertiary italic h-full flex items-center justify-center text-center">
        Click a cell in the grid to edit its velocity, pitch, length, and
        per-step FX sends.
      </div>
    )
  }

  const meta = rowMeta(project, focus.row)
  const cell = meta.cells[focus.stepIndex] ?? { on: false }
  const maxLength = Math.max(1, project.stepCount - focus.stepIndex)
  // Sample-manipulation controls are scoped to sample pads — the
  // engine's reverse/time-stretch/ADSR path only fires for them in
  // the Phase 3 cut. MIDI and procedural-synth pads show the roll
  // section only, since roll fan-out works for any pad kind.
  const padDef =
    focus.row.kind === 'kit' ? project.padDefs?.[focus.row.padIndex] : undefined
  const isSamplePad = padDef?.kind === 'sample'

  return (
    <div className="rounded-xl bg-surface-sunken border border-brd-subtle p-4 flex flex-col gap-4">
      <header className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
            Step {focus.stepIndex + 1} · {meta.label}
          </div>
          <div className="mt-1 text-xs font-mono text-content-secondary">
            {meta.subLabel} · {meta.noteInfo}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {isRichStep(cell) && (
            <button
              type="button"
              onClick={() => clearStepExtras(focus.row, focus.stepIndex)}
              aria-label="Clear per-step velocity, pitch, FX, envelope, and roll"
              title="Clear per-step velocity / pitch / FX / envelope / roll"
              className="grid place-items-center w-7 h-7 rounded-md text-content-tertiary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent transition-colors"
            >
              <BroomIcon size={10} />
            </button>
          )}
          <button
            type="button"
            onClick={() => toggleStep(focus.row, focus.stepIndex)}
            aria-pressed={cell.on}
            className={`px-3 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider transition-colors ${
              cell.on
                ? 'bg-accent text-[color:var(--accent-ink)]'
                : 'bg-surface-raised text-content-secondary border border-brd-subtle hover:border-[color:var(--accent-halo)]'
            }`}
          >
            {cell.on ? 'ON' : 'Off'}
          </button>
        </div>
      </header>

      {/* Phase 3e — always-on CellVisualizer with lane switching.
          Replaces the previous per-dimension accordions (Gain, Tune,
          Timing) so all of velocity / pitch / microOffset / FX live
          in a single surface that stays interactive even when the
          cell is off. Clicking a lane's input flips `on: true`
          automatically — matches the "one unified editor" goal. */}
      <CellVisualizer row={focus.row} stepIndex={focus.stepIndex} cell={cell} />

      {/* Gate — length slider + the dotted/triplet preset pills. */}
      <InspectorSection
        storageKey="seq.insp.gate"
        label="Gate"
        valueChip={
          cell.length !== undefined && cell.length !== 1
            ? `${formatLength(cell.length)} × 16th`
            : undefined
        }
        activeDot={cell.length !== undefined && cell.length !== 1}
      >
        <InspectorRow
          label="Length"
          value={`${formatLength(cell.length ?? 1)} × 16th`}
        >
          <input
            type="range"
            min={0.25}
            max={maxLength}
            step={0.25}
            value={Math.min(maxLength, cell.length ?? 1)}
            onChange={e =>
              setStepLength(focus.row, focus.stepIndex, Number(e.target.value))
            }
            disabled={!cell.on}
            className="w-full accent-[color:var(--accent)]"
          />
          <button
            type="button"
            onClick={() => setStepLength(focus.row, focus.stepIndex, undefined)}
            disabled={cell.length === undefined || cell.length === 1}
            className="text-[10px] font-mono text-content-tertiary hover:text-content-secondary disabled:opacity-40 ml-2"
          >
            reset
          </button>
        </InspectorRow>
        <div className="flex flex-wrap gap-1">
          {LENGTH_PRESETS.map(preset => {
            const active = Math.abs((cell.length ?? 1) - preset.value) < 0.001
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() =>
                  setStepLength(focus.row, focus.stepIndex, preset.value)
                }
                disabled={!cell.on}
                className={`px-1.5 py-0.5 rounded text-[10px] font-mono border transition-colors ${
                  active
                    ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
                    : 'bg-surface-raised text-content-secondary border-brd-subtle hover:border-[color:var(--accent-halo)]'
                } disabled:opacity-40`}
              >
                {preset.label}
              </button>
            )
          })}
        </div>
      </InspectorSection>

      {/* Envelope — now top-level for every pad kind. Engine wires
          per-step ADSR into both sample (scheduleSample) and synth
          (triggerVoice via wrapWithEnvelope) paths, so this editor
          is universal. Reverse + time-stretch stay under the
          sample-only branch below since they only apply to sample
          pads. */}
      <InspectorSection
        storageKey="seq.insp.env"
        label="Envelope (ADSR)"
        defaultOpen={false}
        activeDot={cell.envelope !== undefined}
      >
        <EnvelopeEditor
          cell={cell}
          disabled={!cell.on}
          onChange={envelope =>
            setStepEnvelope(focus.row, focus.stepIndex, envelope)
          }
        />
      </InspectorSection>

      {/* Per-step FX — collapsible, default closed to keep the
          inspector compact. Opens when the user wants per-cell FX
          automation. */}
      <InspectorSection
        storageKey="seq.insp.fx"
        label="FX Sends"
        defaultOpen={false}
        activeDot={FX_IDS.some(id => (cell.fx?.[id] ?? 0) > 0)}
      >
        <div className="flex flex-col gap-2">
          {FX_IDS.map(fxId => {
            const supported = PER_STEP_FX_SUPPORTED.includes(fxId)
            const value = cell.fx?.[fxId] ?? 0
            return (
              <div
                key={fxId}
                className="flex items-center gap-2"
                title={
                  !supported
                    ? 'Filter and tape-stop are master-bus only — use the FX bank below the grid'
                    : undefined
                }
              >
                <span
                  className={`w-16 text-[11px] font-mono ${
                    supported
                      ? 'text-content-secondary'
                      : 'text-content-tertiary/50'
                  }`}
                >
                  {FX_PER_STEP_LABEL[fxId]}
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round(value * 100)}
                  onChange={e =>
                    setStepFx(
                      focus.row,
                      focus.stepIndex,
                      fxId,
                      Number(e.target.value) / 100,
                    )
                  }
                  disabled={!cell.on || !supported}
                  className="flex-1 accent-[color:var(--accent)]"
                />
                <span className="w-8 text-right text-[10px] font-mono text-content-tertiary tabular-nums">
                  {value > 0 ? Math.round(value * 100) : '—'}
                </span>
              </div>
            )
          })}
        </div>
      </InspectorSection>

      {/* Sample-only — reverse + time-stretch. Envelope moved up
          to the top-level Envelope section since the engine now
          applies it to synth voices too. */}
      {isSamplePad && (
        <InspectorSection
          storageKey="seq.insp.sample"
          label="Sample · Reverse / Stretch"
          defaultOpen={false}
          activeDot={
            Boolean(cell.reverse) ||
            (cell.timeStretch !== undefined && cell.timeStretch !== 1)
          }
        >
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-content-secondary select-none">
              <input
                type="checkbox"
                checked={Boolean(cell.reverse)}
                onChange={e =>
                  setStepReverse(focus.row, focus.stepIndex, e.target.checked)
                }
                disabled={!cell.on}
                className="accent-[color:var(--accent)]"
              />
              Reverse
            </label>
          </div>
          <InspectorRow
            label="Time-stretch"
            value={`${(cell.timeStretch ?? 1).toFixed(2)}×`}
          >
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.01}
              value={cell.timeStretch ?? 1}
              onChange={e =>
                setStepTimeStretch(
                  focus.row,
                  focus.stepIndex,
                  Number(e.target.value),
                )
              }
              disabled={!cell.on}
              className="w-full accent-[color:var(--accent)]"
            />
            <button
              type="button"
              onClick={() =>
                setStepTimeStretch(focus.row, focus.stepIndex, undefined)
              }
              disabled={
                cell.timeStretch === undefined || cell.timeStretch === 1
              }
              className="text-[10px] font-mono text-content-tertiary hover:text-content-secondary disabled:opacity-40 ml-2"
            >
              reset
            </button>
          </InspectorRow>
        </InspectorSection>
      )}

      {/* Roll (jungle roll / ratchet). One click gets the user the
          classic rising snare — subdivisions + pitchEnd sliders. */}
      <InspectorSection
        storageKey="seq.insp.roll"
        label="Roll"
        defaultOpen={false}
        activeDot={Boolean(cell.roll)}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono text-content-tertiary w-16">
            Hits
          </span>
          <div className="flex flex-wrap gap-1">
            {ROLL_SUBDIVISIONS.map(n => {
              const active = cell.roll?.subdivisions === n
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() =>
                    setStepRoll(focus.row, focus.stepIndex, {
                      ...(cell.roll ?? {}),
                      subdivisions: n,
                    })
                  }
                  disabled={!cell.on}
                  className={`px-2 py-0.5 rounded text-[10px] font-mono border transition-colors ${
                    active
                      ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
                      : 'bg-surface-raised text-content-secondary border-brd-subtle hover:border-[color:var(--accent-halo)]'
                  } disabled:opacity-40`}
                >
                  {n}
                </button>
              )
            })}
            <button
              type="button"
              onClick={() => setStepRoll(focus.row, focus.stepIndex, undefined)}
              disabled={!cell.roll}
              className="px-2 py-0.5 rounded text-[10px] font-mono border bg-surface-raised text-content-tertiary border-brd-subtle hover:border-[color:var(--accent-halo)] disabled:opacity-40"
            >
              clear
            </button>
          </div>
        </div>
        {cell.roll && (
          <>
            <RollRampEditor
              roll={cell.roll}
              disabled={!cell.on}
              onChange={next => setStepRoll(focus.row, focus.stepIndex, next)}
            />
            <RollMicroGrid
              roll={cell.roll}
              basePitch={cell.pitch ?? 0}
              disabled={!cell.on}
              onChange={next => setStepRoll(focus.row, focus.stepIndex, next)}
            />
          </>
        )}
      </InspectorSection>
    </div>
  )
}

/** Format a length value for the inspector header — keep integers
 *  integer-looking, show meaningful fractions for dotted/triplet. */
function formatLength(n: number): string {
  if (Math.abs(n - Math.round(n)) < 0.001) return String(Math.round(n))
  return n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
}

/**
 * ADSR editor — four horizontal sliders in a compact row. Uses
 * `Partial<Envelope>` so unchanged fields don't overwrite the pad
 * default; writing a value sets it explicitly, "reset" removes the
 * key so the pad default takes over.
 */
const EnvelopeEditor: React.FC<{
  cell: StepCell
  disabled?: boolean
  onChange: (envelope: Partial<Envelope> | undefined) => void
}> = ({ cell, disabled, onChange }) => {
  const env = cell.envelope ?? {}
  const update = (patch: Partial<Envelope>) => {
    const merged: Partial<Envelope> = { ...env, ...patch }
    // Strip keys whose value is undefined so the persisted state
    // doesn't carry explicit `undefined` fields.
    const cleaned: Partial<Envelope> = {}
    for (const k of ['a', 'd', 's', 'r'] as const) {
      if (merged[k] !== undefined) cleaned[k] = merged[k]
    }
    onChange(Object.keys(cleaned).length > 0 ? cleaned : undefined)
  }
  return (
    <div className="flex flex-col gap-2">
      <EnvelopeSlider
        label="Attack"
        unit="s"
        min={0}
        max={2}
        step={0.005}
        value={env.a ?? 0.001}
        isSet={env.a !== undefined}
        disabled={disabled}
        onChange={a => update({ a })}
        onReset={() => update({ a: undefined })}
      />
      <EnvelopeSlider
        label="Decay"
        unit="s"
        min={0}
        max={2}
        step={0.005}
        value={env.d ?? 0}
        isSet={env.d !== undefined}
        disabled={disabled}
        onChange={d => update({ d })}
        onReset={() => update({ d: undefined })}
      />
      <EnvelopeSlider
        label="Sustain"
        unit=""
        min={0}
        max={1}
        step={0.01}
        value={env.s ?? 1}
        isSet={env.s !== undefined}
        disabled={disabled}
        onChange={s => update({ s })}
        onReset={() => update({ s: undefined })}
      />
      <EnvelopeSlider
        label="Release"
        unit="s"
        min={0}
        max={2}
        step={0.005}
        value={env.r ?? 0.02}
        isSet={env.r !== undefined}
        disabled={disabled}
        onChange={r => update({ r })}
        onReset={() => update({ r: undefined })}
      />
    </div>
  )
}

const EnvelopeSlider: React.FC<{
  label: string
  unit: string
  min: number
  max: number
  step: number
  value: number
  isSet: boolean
  disabled?: boolean
  onChange: (v: number) => void
  onReset: () => void
}> = ({
  label,
  unit,
  min,
  max,
  step,
  value,
  isSet,
  disabled,
  onChange,
  onReset,
}) => (
  <div className="flex items-center gap-2">
    <span className="w-16 text-[11px] font-mono text-content-secondary">
      {label}
    </span>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      disabled={disabled}
      className="flex-1 accent-[color:var(--accent)]"
    />
    <span className="w-14 text-right text-[10px] font-mono text-content-tertiary tabular-nums">
      {unit === 's' ? `${(value * 1000).toFixed(0)}ms` : value.toFixed(2)}
    </span>
    <button
      type="button"
      onClick={onReset}
      disabled={disabled || !isSet}
      className="text-[10px] font-mono text-content-tertiary hover:text-content-secondary disabled:opacity-40"
      title="Reset to pad default"
    >
      ⎌
    </button>
  </div>
)

/**
 * Paired start→end sliders for roll pitch and velocity. One drag
 * gets the user a classic jungle snare roll (pitchEnd = +12) or
 * descending bass (pitchEnd = -5) — the paired layout keeps the
 * "Start ramps to End" intent visually obvious without needing
 * a curve editor.
 */
const RollRampEditor: React.FC<{
  roll: StepRoll
  disabled?: boolean
  onChange: (next: StepRoll) => void
}> = ({ roll, disabled, onChange }) => {
  const update = (patch: Partial<StepRoll>) => onChange({ ...roll, ...patch })
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="w-16 text-[11px] font-mono text-content-secondary">
          Pitch
        </span>
        <input
          type="range"
          min={-24}
          max={24}
          step={1}
          value={roll.pitchStart ?? 0}
          onChange={e => update({ pitchStart: Number(e.target.value) })}
          disabled={disabled}
          className="flex-1 accent-[color:var(--accent)]"
        />
        <span className="text-[10px] text-content-tertiary font-mono">→</span>
        <input
          type="range"
          min={-24}
          max={24}
          step={1}
          value={roll.pitchEnd ?? roll.pitchStart ?? 0}
          onChange={e => update({ pitchEnd: Number(e.target.value) })}
          disabled={disabled}
          className="flex-1 accent-[color:var(--accent)]"
        />
        <span className="w-14 text-right text-[10px] font-mono text-content-tertiary tabular-nums">
          {(roll.pitchStart ?? 0) >= 0 ? '+' : ''}
          {roll.pitchStart ?? 0}→
          {(roll.pitchEnd ?? roll.pitchStart ?? 0) >= 0 ? '+' : ''}
          {roll.pitchEnd ?? roll.pitchStart ?? 0}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-16 text-[11px] font-mono text-content-secondary">
          Velocity
        </span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round((roll.velocityStart ?? 1) * 100)}
          onChange={e =>
            update({ velocityStart: Number(e.target.value) / 100 })
          }
          disabled={disabled}
          className="flex-1 accent-[color:var(--accent)]"
        />
        <span className="text-[10px] text-content-tertiary font-mono">→</span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(
            (roll.velocityEnd ?? roll.velocityStart ?? 1) * 100,
          )}
          onChange={e => update({ velocityEnd: Number(e.target.value) / 100 })}
          disabled={disabled}
          className="flex-1 accent-[color:var(--accent)]"
        />
        <span className="w-14 text-right text-[10px] font-mono text-content-tertiary tabular-nums">
          {Math.round((roll.velocityStart ?? 1) * 100)}→
          {Math.round((roll.velocityEnd ?? roll.velocityStart ?? 1) * 100)}
        </span>
      </div>
    </div>
  )
}

/**
 * Per-hit micro-grid editor for a StepRoll. Renders N vertical
 * bars (N = subdivisions) where each bar's height represents the
 * pitch offset applied to that hit (±12 st range). Click the bar
 * to toggle whether that hit fires at all (disabled = dashed
 * outline, no fill). Drag a bar up or down to set its pitch
 * offset.
 *
 * When the user first touches a bar we auto-populate the whole
 * `pitchOffsets` array from the current linear ramp
 * (`pitchStart → pitchEnd`), so explicit per-hit edits start
 * from what the user was already hearing rather than snapping
 * everything to 0. From that point on `pitchOffsets` wins over
 * the ramp in the scheduler.
 */
const RollMicroGrid: React.FC<{
  roll: StepRoll
  /** Base cell pitch in semitones — shown as the reference line
   *  so the user sees how far each bar is from the cell's own
   *  pitch rather than from absolute 0. */
  basePitch: number
  disabled?: boolean
  onChange: (next: StepRoll) => void
}> = ({ roll, basePitch, disabled, onChange }) => {
  const n = roll.subdivisions
  const pStart = roll.pitchStart ?? basePitch
  const pEnd = roll.pitchEnd ?? pStart

  // Seed the per-hit arrays on first edit. Without this, touching
  // one bar would zero out every other hit's pitch (the linear
  // ramp is implicit — an explicit `pitchOffsets` array has to
  // carry every hit). Derives from the current ramp so existing
  // rising / falling rolls survive the migration unchanged.
  const seedOffsets = (): number[] =>
    Array.from({ length: n }, (_, i) =>
      n <= 1 ? pStart : pStart + (pEnd - pStart) * (i / (n - 1)),
    )
  const seedEnabled = (): boolean[] => Array.from({ length: n }, () => true)

  const currentOffsets =
    roll.pitchOffsets && roll.pitchOffsets.length === n
      ? roll.pitchOffsets
      : seedOffsets()
  const currentEnabled =
    roll.subdivisionEnabled && roll.subdivisionEnabled.length === n
      ? roll.subdivisionEnabled
      : seedEnabled()

  const setOffset = (i: number, semitones: number) => {
    const next = [...currentOffsets]
    next[i] = Math.max(-12, Math.min(12, Math.round(semitones)))
    onChange({ ...roll, pitchOffsets: next })
  }
  const toggleEnabled = (i: number) => {
    const next = [...currentEnabled]
    next[i] = !next[i]
    onChange({ ...roll, subdivisionEnabled: next })
  }
  const clearMicroOverrides = () => {
    const { pitchOffsets: _po, subdivisionEnabled: _se, ...rest } = roll
    void _po
    void _se
    onChange(rest)
  }

  const hasOverrides =
    (roll.pitchOffsets && roll.pitchOffsets.length > 0) ||
    (roll.subdivisionEnabled && roll.subdivisionEnabled.some(v => v === false))

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          Micro-grid
        </span>
        <button
          type="button"
          onClick={clearMicroOverrides}
          disabled={disabled || !hasOverrides}
          title="Reset per-hit pitch offsets + enable mask — fall back to the linear ramp"
          className="text-[9px] font-mono text-content-tertiary hover:text-content-secondary disabled:opacity-40"
        >
          reset
        </button>
      </div>
      <div
        className="relative grid gap-[3px] bg-surface-base border border-brd-subtle rounded-md p-2"
        style={{
          gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
          height: 96,
        }}
      >
        {/* Zero-line reference — where a 0-semitone hit would
            land. Visual anchor so up vs down reads correctly at
            a glance. */}
        <div
          aria-hidden
          className="absolute left-2 right-2 top-1/2 h-px bg-content-tertiary/30 pointer-events-none"
        />
        {Array.from({ length: n }).map((_, i) => {
          const st = currentOffsets[i] ?? 0
          const enabled = currentEnabled[i] !== false
          // Bar height / position — map -12..+12 st into 0..100 %
          // of the container height, anchored on the zero line.
          // Positive offsets extend upward from centre, negative
          // extend downward. Absolute-positioned child inside the
          // grid cell so bars snap to the cell's width but align
          // vertically by percentage.
          const pct = Math.max(-12, Math.min(12, st)) / 12 // -1..+1
          const height = Math.abs(pct) * 50 // 0..50% of cell height
          return (
            <div key={i} className="relative h-full flex flex-col items-center">
              {/* Click-to-toggle header cap — press inverts enable
                  state. The cap sits at the top so it's reachable
                  even when the bar is pointing far downward. */}
              <button
                type="button"
                onClick={() => !disabled && toggleEnabled(i)}
                disabled={disabled}
                title={
                  enabled
                    ? `Hit ${i + 1} · ${st > 0 ? '+' : ''}${st} st — click to mute`
                    : `Hit ${i + 1} muted — click to enable`
                }
                className={`absolute inset-x-0.5 top-0 h-3 rounded-sm text-[8px] font-mono leading-none z-10 ${
                  enabled
                    ? 'bg-content-tertiary/30 hover:bg-accent/50 text-content-tertiary hover:text-[color:var(--accent-ink)]'
                    : 'bg-transparent border border-dashed border-content-tertiary/50 text-content-tertiary/60'
                } disabled:opacity-40`}
              >
                {i + 1}
              </button>
              {/* Pitch bar — absolute-positioned from the cell's
                  vertical centre so positive vs negative read
                  intuitively. Drag is a native range input
                  rotated vertically; height via inline style. */}
              <div
                aria-hidden
                className={`absolute left-1 right-1 rounded-sm transition-colors ${
                  enabled
                    ? 'bg-accent'
                    : 'bg-transparent border border-dashed border-content-tertiary/40'
                }`}
                style={{
                  top: pct < 0 ? '50%' : `${50 - height}%`,
                  bottom: pct > 0 ? '50%' : `${50 - height}%`,
                  opacity: enabled ? 0.85 : 0.5,
                }}
              />
              {/* Invisible range handles the actual edit. Rotated
                  90° so "up = raise pitch" via the slider's
                  native track. Hit-area is the full cell, so the
                  user can grab anywhere to drag. */}
              <input
                type="range"
                min={-12}
                max={12}
                step={1}
                value={st}
                onChange={e => setOffset(i, Number(e.target.value))}
                disabled={disabled || !enabled}
                aria-label={`Pitch offset for hit ${i + 1}`}
                className="absolute inset-0 top-3 opacity-0 cursor-ns-resize disabled:cursor-not-allowed"
                // Vertical range semantics: drag vertically to
                // change value. Native `<input type="range">` with
                // CSS writing-mode: vertical-lr gives us this on
                // modern browsers.
                style={{
                  writingMode:
                    'vertical-lr' as React.CSSProperties['writingMode'],
                  direction: 'rtl',
                }}
              />
              {/* Readout at bottom — small and dim so it doesn't
                  compete with the bar itself. */}
              <span className="absolute bottom-0 left-0 right-0 text-center text-[8px] font-mono text-content-tertiary/80 tabular-nums">
                {st > 0 ? '+' : ''}
                {st}
              </span>
            </div>
          )
        })}
      </div>
      <p className="text-[9px] text-content-tertiary/80 italic leading-snug">
        Drag any bar vertically to set that hit's pitch offset. Click the number
        cap to mute a hit. Reset restores the linear pitch ramp.
      </p>
    </div>
  )
}

const InspectorRow: React.FC<{
  label: string
  value: string
  children: React.ReactNode
}> = ({ label, value, children }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1">
      <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
        {label}
      </span>
      <span className="text-[10px] font-mono text-content-secondary tabular-nums">
        {value}
      </span>
    </div>
    <div className="flex items-center">{children}</div>
  </div>
)
